import type { ReactNode } from 'react'
import { Check, CheckCircle2, Loader2, SkipForward, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Overlay'
import { EmptyState } from '@/components/ui/States'
import { countLabel } from '@/lib/format'
import type { ReviewStatus } from '@/types'

/**
 * The popup that walks the pending queue one item at a time (APP-140). The
 * entity supplies the editable body; the shell owns the progress line, the
 * spinner while a verdict saves, the done state and the footer, so quotes
 * and media get the same gesture.
 *
 * Closing at any point ends the run. Nothing is saved on close or on skip.
 */
export function ReviewQueueModal({
  open,
  onClose,
  noun,
  title,
  subtitle,
  remaining,
  done,
  busy,
  onApprove,
  onReject,
  onSkip,
  children,
}: {
  open: boolean
  onClose: () => void
  /** Singular. "program" → "3 programs left". */
  noun: string
  title: string
  subtitle?: ReactNode
  remaining: number
  done: boolean
  /** The verdict being saved, or null. */
  busy: Exclude<ReviewStatus, 'pending'> | null
  onApprove: () => void
  onReject: () => void
  onSkip: () => void
  children: ReactNode
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={done ? `No pending ${noun}s left` : title}
      description={done ? undefined : subtitle}
      width="max-w-[720px]"
      footer={
        done ? (
          <Button size="sm" variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <span className="mr-auto flex items-center gap-3">
              <Button size="sm" variant="ghost" onClick={onClose} disabled={busy !== null}>
                Exit review
              </Button>
              <span className="text-[12px] text-ink-faint">{countLabel(remaining, noun)} left</span>
            </span>
            <Button size="sm" onClick={onSkip} disabled={busy !== null}>
              <SkipForward size={12} aria-hidden />
              Skip
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={onReject}
              loading={busy === 'rejected'}
              disabled={busy !== null}
            >
              {busy !== 'rejected' && <X size={12} aria-hidden />}
              Reject
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={onApprove}
              loading={busy === 'approved'}
              disabled={busy !== null}
            >
              {busy !== 'approved' && <Check size={12} aria-hidden />}
              Accept
            </Button>
          </>
        )
      }
    >
      {done ? (
        <EmptyState
          icon={<CheckCircle2 size={22} />}
          title="You are through the queue"
          description={`Skipped ${noun}s stay pending for the next review.`}
        />
      ) : (
        <div className="relative">
          <div
            className="max-h-[60vh] space-y-4 overflow-y-auto pb-3 pr-1"
            aria-busy={busy !== null || undefined}
            inert={busy !== null || undefined}
          >
            {children}
          </div>
          {busy && (
            <div className="absolute inset-0 flex items-center justify-center bg-surface/70" role="status">
              <Loader2 size={22} className="animate-spin text-ink-muted" aria-hidden />
              <span className="sr-only">Saving</span>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
